import { useEffect, useRef, useState } from 'react';
import { CardNumber, ExpiryDate, SecurityCode, Field } from '@gr4vy/secure-fields-react';
import cssClass from 'classnames';

import type { ISecurePaymentFieldsProps, InputWithClear } from './SecurePaymentFields.types';
import { StyledSecurePaymentFields, StyledInputDiv, StyledError } from './SecurePaymentFields.styles';
import { cardTypes } from '../constants';

export const SecurePaymentFields = ({
  formStatus,
  cardProps,
  onNickNameChange,
  showZipCodeField,
  showCvvNumberField,
  onCardNumberChange,
  enableInputFieldV3 = false,
}: ISecurePaymentFieldsProps) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [focusedField, setFocusedField] = useState<'number' | 'expiry' | 'securityCode' | 'postalCode' | null>(null);
  const [clearedFields, setClearedFields] = useState({
    number: false,
    expiry: false,
    securityCode: false,
    postalCode: false,
  });

  const isFocused = (field: typeof focusedField) => focusedField === field;

  const v3FieldStyles = enableInputFieldV3 ? { padding: '0' as const } : undefined;

  const { fields } = formStatus;

  const { valid: validCardNumber, empty: isEmptyCardNumber } = fields.number;
  const { valid: validExpiryDate, empty: isEmptyExpiry } = fields.expiryDate;
  const { valid: validSecurityCode, empty: isEmptySecurityCode } = fields.securityCode;
  const { valid: validPostalCode, empty: isEmptyPostalCode } = fields.postalCode || {};

  const { nickName, cardNumber, expiryDate, cvv, billingZipCode } = cardProps || {};

  const showCardNumberError = !validCardNumber || clearedFields.number;
  const showExpiryDateError = !validExpiryDate || clearedFields.expiry;
  const showSecurityCodeError = !validSecurityCode || clearedFields.securityCode;
  const showPostalCodeError = !validPostalCode || clearedFields.postalCode;

  const ccNickNameClassName = cssClass('input-box', { 'input-box--v3': enableInputFieldV3 });
  const cardNumberClassName = cssClass('input-box', {
    'input-box--v3 input-box--v3--card-number': enableInputFieldV3,
    'is-error': showCardNumberError,
  });
  const expiryDateClassName = cssClass('input-box', {
    'input-box--v3 input-box--v3--expiry-date': enableInputFieldV3,
    'is-error': showExpiryDateError,
  });
  const securityCodeClassName = cssClass('input-box input-with-icon', {
    'input-box--v3': enableInputFieldV3,
    'is-error': showSecurityCodeError,
  });
  const postalClassName = cssClass('input-box', {
    'input-box--v3 input-box--v3--postal-code': enableInputFieldV3,
    'is-error': showPostalCodeError,
  });
  const columnClassName = cssClass('details-grid', {
    'two-columns': (showCvvNumberField && !showZipCodeField) || (!showCvvNumberField && showZipCodeField),
    'one-column': !showCvvNumberField && !showZipCodeField,
  });

  const renderErrorMessage = (isEmpty: boolean = false, errorProps: { emptyError: string; invalidError: string }) => {
    const { emptyError, invalidError } = errorProps;
    return <StyledError>{isEmpty ? emptyError : invalidError}</StyledError>;
  };

  // Refs for Secure Field inputs. Used to access the SDK-provided `clear()` method to programmatically reset field values
  // Ref for Card Number, Expiry Date and Postal Code secure fields, to enable clearing the entered values respectively
  const cardNumberRef = useRef<InputWithClear | null>(null) as React.MutableRefObject<InputWithClear>;
  const expiryDateRef = useRef<InputWithClear | null>(null) as React.MutableRefObject<InputWithClear>;
  const postalCodeRef = useRef<InputWithClear | null>(null) as React.MutableRefObject<InputWithClear>;
  const cvvCodeRef = useRef<InputWithClear | null>(null) as React.MutableRefObject<InputWithClear>;

  // Use mouse down so clear executes before secure field blur unmounts the icon.
  const handleClear =
    (ref: React.MutableRefObject<InputWithClear | null>, field: 'number' | 'expiry' | 'securityCode' | 'postalCode') =>
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      setClearedFields(prev => ({ ...prev, [field]: true }));
      ref.current?.clear?.();
    };

  // to show the tooltip only if the cvv field is empty
  useEffect(() => {
    if (!fields.securityCode.empty) {
      setShowTooltip(false);
    }
  }, [fields.securityCode.empty]);

  // Keep inline error visible after clear icon click; remove override when user starts typing again.
  useEffect(() => {
    setClearedFields(prev => ({
      ...prev,
      number: fields.number.empty ? prev.number : false,
      expiry: fields.expiryDate.empty ? prev.expiry : false,
      securityCode: fields.securityCode.empty ? prev.securityCode : false,
      postalCode: fields.postalCode?.empty ? prev.postalCode : false,
    }));
  }, [fields.number.empty, fields.expiryDate.empty, fields.securityCode.empty, fields.postalCode?.empty]);

  return (
    <StyledSecurePaymentFields>
      {nickName?.showNickName && (
        <div className="card-nickname">
          <StyledInputDiv>
            <div className={ccNickNameClassName}>
              <label htmlFor="cc-nickname">{nickName?.label}</label>
              <input
                type="text"
                id="cc-nickname"
                name="nickName"
                className="cardNickName"
                placeholder={nickName?.placeholder}
                onChange={e => onNickNameChange(e?.target?.value)}
              />
            </div>
          </StyledInputDiv>
        </div>
      )}

      <div className="card-number-row">
        <StyledInputDiv>
          <div className={cardNumberClassName}>
            <label htmlFor="cc-number">{cardNumber?.label}</label>
            <div className="card-number-wrapper">
              {' '}
              <CardNumber
                ref={cardNumberRef}
                placeholder={cardNumber?.placeholder}
                styles={v3FieldStyles}
                onInput={({ schema }: { schema: keyof typeof cardTypes }) => onCardNumberChange(schema)}
                onFocus={() => setFocusedField('number')}
                onBlur={() => setFocusedField(null)}
              />
              {isFocused('number') && (
                <span className="icon-cancel" onMouseDown={handleClear(cardNumberRef, 'number')} />
              )}
            </div>
          </div>
          {showCardNumberError && renderErrorMessage(clearedFields.number || isEmptyCardNumber, cardNumber)}
        </StyledInputDiv>
      </div>

      <div className={columnClassName}>
        <StyledInputDiv>
          <div className={expiryDateClassName}>
            <label htmlFor="cc-expiry-date">{expiryDate?.label}</label>
            <div className="card-number-wrapper" id="expiry-date">
              <ExpiryDate
                ref={expiryDateRef}
                styles={v3FieldStyles}
                placeholder={expiryDate?.placeholder}
                onFocus={() => setFocusedField('expiry')}
                onBlur={() => setFocusedField(null)}
              />
              {isFocused('expiry') && (
                <span className="icon-cancel" onMouseDown={handleClear(expiryDateRef, 'expiry')} />
              )}
            </div>
          </div>
          {showExpiryDateError && renderErrorMessage(clearedFields.expiry || isEmptyExpiry, expiryDate)}{' '}
        </StyledInputDiv>

        {showCvvNumberField && (
          <StyledInputDiv>
            <div className={securityCodeClassName}>
              <label htmlFor="cc-security-code">{cvv?.label}</label>
              <div className="card-number-wrapper" id="cvv-number">
                <SecurityCode
                  ref={cvvCodeRef}
                  placeholder={cvv?.placeholder}
                  styles={v3FieldStyles}
                  onFocus={() => setFocusedField('securityCode')}
                  onBlur={() => setFocusedField(null)}
                />

                {/* Cancel icon: show only when focused */}
                {isFocused('securityCode') && (
                  <span className="icon-cancel" onMouseDown={handleClear(cvvCodeRef, 'securityCode')} />
                )}

                {/* Info icon: show ONLY when empty AND NOT focused */}
                {cvv.showCVVTooltip && isEmptySecurityCode && !isFocused('securityCode') && (
                  <span className="icon-information" onClick={() => setShowTooltip(true)} />
                )}

                {/* Tooltip */}
                {showTooltip && isEmptySecurityCode && !isFocused('securityCode') && (
                  <>
                    <div className="cvv-tooltip">
                      <div className="tooltip-header">
                        <span>{cvv?.label}</span>
                        <button type="button" className="close-icon" onClick={() => setShowTooltip(false)}>
                          <span className="icon-clear" />
                        </button>
                      </div>
                      <p>{cvv?.toolTip}</p>
                    </div>

                    <span className="tooltip-pointer" />
                  </>
                )}
              </div>
            </div>
            {showSecurityCodeError && renderErrorMessage(clearedFields.securityCode || isEmptySecurityCode, cvv)}
          </StyledInputDiv>
        )}

        {showZipCodeField && (
          <StyledInputDiv>
            <div className={postalClassName}>
              <label htmlFor="postal-code">{billingZipCode?.label}</label>
              <div className="card-number-wrapper" id="postal-code">
                <Field
                  ref={postalCodeRef}
                  type="postalCode"
                  placeholder={billingZipCode?.placeholder}
                  styles={v3FieldStyles}
                  onFocus={() => setFocusedField('postalCode')}
                  onBlur={() => setFocusedField(null)}
                />

                {isFocused('postalCode') && (
                  <span className="icon-cancel" onMouseDown={handleClear(postalCodeRef, 'postalCode')} />
                )}
              </div>
            </div>
            {showPostalCodeError && renderErrorMessage(clearedFields.postalCode || isEmptyPostalCode, billingZipCode)}
          </StyledInputDiv>
        )}
      </div>
    </StyledSecurePaymentFields>
  );
};
